import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { AssignPreviewPayload } from "@/features/leads/types/assignPreview";

type AskPayload = Extract<AssignPreviewPayload, { mode: "ask" }>;

type Props = {
  open: boolean;
  onClose: () => void;
  ask: AskPayload | null;
  isLoading?: boolean;
  onChooseLead: () => void;
  onChooseChild: (childId: number) => void;
};

/**
 * Shown when the server cannot auto-resolve lead vs child placement for this role.
 */
export default function AssignLevelPopup({
  open,
  onClose,
  ask,
  isLoading,
  onChooseLead,
  onChooseChild,
}: Props) {
  const [childPickStep, setChildPickStep] = useState(false);
  const [selectedChildId, setSelectedChildId] = useState<number | "">("");

  if (!ask) return null;

  const { leadOption, childOption } = ask;
  const canLead = leadOption.available;
  const canChild = childOption.available;
  const children = childOption.children ?? [];
  const defaultChildId =
    childOption.activeChildId ??
    (children.length === 1 ? children[0].id : undefined);

  const resetAndClose = () => {
    setChildPickStep(false);
    setSelectedChildId("");
    onClose();
  };

  const handleChildClick = () => {
    if (!canChild) return;
    if (defaultChildId != null) {
      onChooseChild(defaultChildId);
      resetAndClose();
      return;
    }
    if (children.length > 1) {
      setChildPickStep(true);
    }
  };

  const submitChildPicker = () => {
    if (selectedChildId === "" || selectedChildId == null) return;
    onChooseChild(Number(selectedChildId));
    resetAndClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) resetAndClose();
      }}
    >
      <DialogContent className="p-5 flex flex-col gap-3 max-w-md">
        <DialogHeader>
          <p className="text-[16px] font-medium text-[#1e302a]">
            Assign as lead or application
          </p>
          <p className="text-[13px] text-[#697571] font-normal">
            This role is not yet on this lead. Should this person be on the
            whole lead, or on one application (deal)?
          </p>
        </DialogHeader>

        {childPickStep && children.length > 1 ? (
          <div className="flex flex-col gap-2">
            <label className="text-[12px] text-[#697571]">Application</label>
            <select
              className="w-full border border-[#e6e8e7] rounded-lg px-3 py-2 text-[13px] bg-white"
              value={selectedChildId}
              onChange={(e) =>
                setSelectedChildId(
                  e.target.value === "" ? "" : Number(e.target.value),
                )
              }
            >
              <option value="">Select application</option>
              {children.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                type="button"
                variant="cancel"
                onClick={() => setChildPickStep(false)}
              >
                Back
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={submitChildPicker}
                disabled={selectedChildId === ""}
              >
                Continue
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-2">
              <Button
                type="button"
                variant="secondary"
                className="w-full justify-center py-3 h-auto"
                onClick={() => {
                  onChooseLead();
                  resetAndClose();
                }}
                disabled={!canLead || isLoading}
              >
                Lead user
              </Button>
              {!canLead && "reason" in leadOption && (
                <p className="text-[12px] text-amber-700">{leadOption.reason}</p>
              )}

              <Button
                type="button"
                variant="secondary"
                className="w-full justify-center py-3 h-auto"
                onClick={handleChildClick}
                disabled={!canChild || isLoading}
              >
                Application (deal) user
              </Button>
              {!canChild && childOption.reason && (
                <p className="text-[12px] text-amber-700">{childOption.reason}</p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="cancel" onClick={resetAndClose}>
                Cancel
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
